import { ZodIssueSchema, type WireError, type WireZodIssue } from '@immo/api-contract';

/**
 * Frontend error classes per ADR 0014. They mirror the backend's wire
 * contract structurally — `{ id?, code?, message?, details? }` plus the HTTP
 * status from the response line — but are FE-only constructs: the parser is
 * the only thing that builds them, and the rest of the app pattern-matches on
 * them with `instanceof`.
 *
 * The wire `message` is carried as `wireMessage` and is NEVER rendered to a
 * user (ADR 0009 / 0014) — it lives in telemetry only. User-facing copy comes
 * from the i18n dictionary keyed by `code`.
 */

const GENERIC_CODE = 'errors.generic.unexpected';
export const FORBIDDEN_CODE = 'errors.generic.forbidden';
export const OFFLINE_CODE = 'errors.network.offline';
export const UNREACHABLE_CODE = 'errors.network.unreachable';

/**
 * A transport failure has two very different causes for the user: their own
 * connection is down, or ours is. `navigator.onLine === false` is the only
 * reliable half of that signal — `true` merely means the browser has *a*
 * network, not that the API answered — so "you are offline" is claimed only
 * when the browser positively reports the link is down, and everything else
 * (the API blipping, DNS, a reset connection) gets the neutral unreachable copy.
 */
function networkCode(): string {
  return typeof navigator !== 'undefined' && navigator.onLine === false ? OFFLINE_CODE : UNREACHABLE_CODE;
}

export type ApiErrorInit = {
  status: number;
  id?: string;
  code?: string;
  wireMessage?: string;
  details?: Record<string, unknown>;
};

export abstract class ApiError extends Error {
  readonly status: number;
  readonly id?: string;
  readonly code?: string;
  readonly wireMessage?: string;
  readonly details?: Record<string, unknown>;

  protected constructor(name: string, init: ApiErrorInit) {
    super(init.code ?? name);
    this.name = name;
    this.status = init.status;

    if (init.id !== undefined) this.id = init.id;

    if (init.code !== undefined) this.code = init.code;

    if (init.wireMessage !== undefined) this.wireMessage = init.wireMessage;

    if (init.details !== undefined) this.details = init.details;
  }
}

type WireErrorBody = WireError['error'];

function initFrom(status: number, body: WireErrorBody): ApiErrorInit {
  return {
    status,
    ...(body.id !== undefined ? { id: body.id } : {}),
    ...(body.code !== undefined ? { code: body.code } : {}),
    ...(body.message !== undefined ? { wireMessage: body.message } : {}),
    ...(body.details !== undefined ? { details: body.details } : {}),
  };
}

export class ValidationError extends ApiError {
  constructor(body: WireErrorBody) {
    super('ValidationError', initFrom(400, body));
  }

  issues(): WireZodIssue[] {
    const raw = this.details?.['issues'];

    if (!Array.isArray(raw)) return [];

    return raw.flatMap((issue) => {
      const parsed = ZodIssueSchema.safeParse(issue);

      return parsed.success ? [parsed.data] : [];
    });
  }
}

export class UnauthorizedError extends ApiError {
  constructor(body: WireErrorBody) {
    super('UnauthorizedError', initFrom(401, body));
  }
}

export class ForbiddenError extends ApiError {
  constructor(body: WireErrorBody) {
    super('ForbiddenError', initFrom(403, body));
  }
}

export class NotFoundError extends ApiError {
  constructor(body: WireErrorBody) {
    super('NotFoundError', initFrom(404, body));
  }
}

export class BusinessRuleError extends ApiError {
  constructor(body: WireErrorBody) {
    super('BusinessRuleError', initFrom(422, body));
  }
}

/**
 * Any HTTP status >= 500. Carries the wire `error.id` (the backend mints one
 * for reportable failures). The code defaults to the generic key so the
 * dictionary lookup always resolves — a sanitised `ThirdPartyError` (e.g. a
 * 502 from an upstream outage) reaches the FE with no `code` per ADR 0013.
 */
export class ServerError extends ApiError {
  constructor(status: number, body: WireErrorBody) {
    super('ServerError', { ...initFrom(status, body), code: body.code ?? GENERIC_CODE });
  }
}

/** No HTTP response at all (fetch threw, offline, aborted). Client-minted id. */
export class NetworkError extends ApiError {
  override readonly cause?: unknown;

  constructor(opts: { cause?: unknown } = {}) {
    super('NetworkError', { status: 0, id: crypto.randomUUID(), code: networkCode() });

    if (opts.cause !== undefined) this.cause = opts.cause;
  }
}

/** A response arrived but its body did not conform to `WireErrorSchema`. Client-minted id. */
export class UnknownApiError extends ApiError {
  constructor(opts: { status: number }) {
    super('UnknownApiError', { status: opts.status, id: crypto.randomUUID(), code: GENERIC_CODE });
  }
}
